// utils/fileValidation.js
// File validation utilities for document uploads

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
];

const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];

/**
 * Validate a single file
 * @param {File} file - File to validate
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateFile = (file) => {
  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { 
      valid: false, 
      error: `File "${file.name}" exceeds maximum size of ${MAX_FILE_SIZE / 1024 / 1024}MB` 
    };
  }

  // Check file type
  const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
    return { 
      valid: false, 
      error: `File "${file.name}" has unsupported format. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}` 
    };
  }

  // Check MIME type (additional validation)
  if (file.type && !ALLOWED_FILE_TYPES.includes(file.type)) {
    // Some browsers might not set type correctly, so only warn if type is set
    console.warn(`File "${file.name}" has unexpected MIME type: ${file.type}`);
  }

  // Check if file is empty
  if (file.size === 0) {
    return { 
      valid: false, 
      error: `File "${file.name}" is empty` 
    };
  }

  return { valid: true, error: null };
};

/**
 * Validate multiple files
 * @param {Array<File>} files - Array of files to validate
 * @returns {Object} - { valid: Array<File>, errors: Array<string> }
 */
export const validateFiles = (files) => {
  const validFiles = [];
  const errors = [];

  files.forEach(file => {
    const validation = validateFile(file);
    if (validation.valid) {
      validFiles.push(file);
    } else {
      errors.push(validation.error);
    }
  });

  return { valid: validFiles, errors };
};

/**
 * Get file type icon
 * @param {string} fileName - File name
 * @returns {string} - Icon emoji
 */
export const getFileIcon = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  
  const iconMap = {
    pdf: '📕',
    doc: '📘',
    docx: '📘',
    txt: '📄'
  };

  return iconMap[extension] || '📎';
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Check if file is a legal document type
 * @param {string} fileName - File name
 * @returns {boolean} - True if legal document type
 */
export const isLegalDocument = (fileName) => {
  const legalKeywords = [
    'agreement', 'contract', 'deed', 'certificate',
    'rera', 'noc', 'title', 'lease', 'sale',
    'mortgage', 'loan', 'legal', 'property'
  ];

  const lowerFileName = fileName.toLowerCase();
  return legalKeywords.some(keyword => lowerFileName.includes(keyword));
};

/**
 * Extract document metadata from filename
 * @param {string} fileName - File name
 * @returns {Object} - Extracted metadata
 */
export const extractDocumentMetadata = (fileName) => {
  const metadata = {
    type: 'Unknown',
    keywords: []
  };

  const lowerFileName = fileName.toLowerCase();

  // Detect document type
  if (lowerFileName.includes('agreement') || lowerFileName.includes('contract')) {
    metadata.type = 'Agreement/Contract';
  } else if (lowerFileName.includes('rera')) {
    metadata.type = 'RERA Certificate';
  } else if (lowerFileName.includes('title') || lowerFileName.includes('deed')) {
    metadata.type = 'Title Deed';
  } else if (lowerFileName.includes('noc')) {
    metadata.type = 'NOC';
  } else if (lowerFileName.includes('lease')) {
    metadata.type = 'Lease Agreement';
  } else if (lowerFileName.includes('sale')) {
    metadata.type = 'Sale Document';
  }

  // Extract keywords
  const keywordPatterns = [
    'property', 'land', 'apartment', 'flat', 'villa',
    'builder', 'developer', 'bank', 'loan',
    'payment', 'installment', 'possession'
  ];

  metadata.keywords = keywordPatterns.filter(keyword => 
    lowerFileName.includes(keyword)
  );

  return metadata;
};

export default {
  validateFile,
  validateFiles,
  getFileIcon,
  formatFileSize,
  isLegalDocument,
  extractDocumentMetadata,
  MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES,
  ALLOWED_EXTENSIONS
};
